/**
 * Main App component with routing and providers
 */

import React, { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuthContext } from './components/Auth/AuthProvider';
import { LoginForm } from './components/Auth/LoginForm';
import { AppShell } from './components/Layout/AppShell';
import { PluginManager } from './plugins/PluginManager';
import { PluginContextProvider } from './plugins/PluginContext';
import { SearchConfigProvider } from './components/Search/SearchConfigProvider';
import { EntityConfigProvider } from './components/Entities/EntityConfigProvider';
import { ErrorBoundary } from './components/UI/ErrorBoundary';
import { TraceKitConfig } from './types/config';
import { pluginRegistry } from './plugins/PluginRegistry';
import { initializeAPIClient } from './api/client';
import { getBaseUrl } from './utils/env';
import { useThemeStore, applyTheme } from './stores/theme';
import { ThemeProvider } from './contexts/ThemeContext';
import { ConfigProvider } from './contexts/ConfigContext';
import { applyThemeVariables } from './utils/theme';

// Components
import { HomeDashboard } from './components/Home/HomeDashboard';
import { ChatInterface } from './components/RAG/ChatInterface';
import { UniversalSearch } from './components/Search/UniversalSearch';
import { KnowledgeBase } from './components/Knowledge/KnowledgeBase';
import { EntityExplorer } from './components/Entities/EntityExplorer';
import { ReportBuilder } from './components/Reports/ReportBuilder';
import { ReportsList } from './components/Reports';
import { SourceList } from './components/Sources/SourceList';
import { JobList } from './components/Jobs/JobList';
import { DataManagementDashboard } from './pages/DataManagementDashboard';
import { Settings } from './components/Admin/Settings';
import { EvidenceViewer } from './components/Evidence/EvidenceViewer';
import { PolicyManager } from './components/Policy/PolicyManager';
import { UserList } from './components/Users/UserList';
import { TeamManagement } from './components/Teams/TeamManagement';
import { AuditLogsViewer } from './components/Audit/AuditLogsViewer';
import { RolesManager } from './components/RBAC/RolesManager';
import { OAuthCallback } from './pages/OAuthCallback';
import { AcceptInvitation } from './pages/AcceptInvitation';
import { ResetPassword } from './pages/ResetPassword';
import { Profile } from './pages/Profile';
import { NotificationCenter } from './components/Notifications/NotificationCenter';
import { NotificationPreferences } from './components/Notifications/NotificationPreferences';
import { Button } from './components/UI/Button';
import { Card } from './components/UI/Card';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 30000,
    },
  },
});

function AppContent({ config }: { config: TraceKitConfig }) {
  const { isAuthenticated } = useAuthContext();
  const { getEffectiveTheme, setTheme } = useThemeStore();
  
  // Initialize theme on mount and when theme changes
  useEffect(() => {
    // Set default theme mode from config
    if (config.theme?.defaultMode) {
      const { defaultMode } = config.theme;
      if (defaultMode === 'light' || defaultMode === 'dark') {
        setTheme(defaultMode);
      }
    }
    
    // Get current theme and apply it
    const store = useThemeStore.getState();
    const effectiveTheme = store.getEffectiveTheme();
    applyTheme(effectiveTheme);
    
    // Subscribe to theme changes
    const unsubscribe = useThemeStore.subscribe((state) => {
      const newEffectiveTheme = state.getEffectiveTheme();
      applyTheme(newEffectiveTheme);
    });
    
    return unsubscribe;
  }, [config.theme, setTheme]);
  
  // Get base URL from Vite config (e.g., '/pfas-cfb-comox/' or '/')
  const baseUrl = getBaseUrl();
  
  // Helper to normalize paths by removing base URL prefix
  const normalizePath = (fullPath: string): string => {
    if (baseUrl === '/') return fullPath;
    if (fullPath.startsWith(baseUrl)) {
      return fullPath.substring(baseUrl.length - 1); // Keep leading slash
    }
    return fullPath;
  };
  
  // Helper to add base URL prefix for navigation
  const addBasePath = (path: string): string => {
    if (baseUrl === '/') return path;
    return baseUrl.replace(/\/$/, '') + path;
  };
  
  const [currentPath, setCurrentPath] = useState(normalizePath(window.location.pathname));

  // Initialize search config and entity config from app config BEFORE anything else mounts
  useEffect(() => {
    if (config.customization?.searchSampleQueries) {
      console.log('[App] Setting search sample queries:', config.customization.searchSampleQueries);
      (window as any).__tracekit_search_queries = config.customization.searchSampleQueries;
    }
    
    if (config.customization?.entityTypes) {
      console.log('[App] Setting entity types:', config.customization.entityTypes);
      (window as any).__tracekit_entity_types = config.customization.entityTypes;
    }
    
    // Force providers to pick up changes
    window.dispatchEvent(new CustomEvent('tracekitConfigReady'));
  }, [config]);

  // Sync with browser history
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(normalizePath(window.location.pathname));
    };

    const handleNavigate = (e: CustomEvent<{ path: string }>) => {
      const path = e.detail.path;
      setCurrentPath(path);
      window.history.pushState({}, '', addBasePath(path));
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('navigate', handleNavigate as EventListener);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('navigate', handleNavigate as EventListener);
    };
  }, []);

  // Navigate function that updates both state and URL
  const handleNavigate = (path: string) => {
    setCurrentPath(path);
    window.history.pushState({}, '', addBasePath(path));
  };

  // Routes that are accessible without authentication (and should never show sidebar)
  const publicRoutes = ['/login', '/reset-password', '/accept-invitation', '/auth/callback'];
  const isPublicRoute = publicRoutes.some(route => currentPath.startsWith(route));

  // Redirect logic: unauthenticated users to /login, authenticated users on /login to home
  // BUT: Don't redirect if we just logged out (check for logout query param or recent logout)
  useEffect(() => {
    if (!isAuthenticated && !isPublicRoute) {
      handleNavigate('/login');
    } else if (isAuthenticated && currentPath === '/login') {
      // Check if we're coming from a logout - if so, don't redirect
      const urlParams = new URLSearchParams(window.location.search);
      const isLogout = urlParams.get('logout') === 'true';
      
      // Also check if we just navigated to login (might be from logout)
      // Only redirect authenticated users away from login if they didn't just log out
      if (!isLogout) {
        // Redirect authenticated users away from login page to home
        handleNavigate('/');
      } else {
        // Clear the logout param from URL after a brief delay to ensure auth state has cleared
        setTimeout(() => {
          window.history.replaceState({}, '', '/login');
        }, 100);
      }
    }
  }, [isAuthenticated, isPublicRoute, currentPath]);

  // Show public routes without AppShell (no sidebar)
  if (isPublicRoute) {
    switch (currentPath) {
      case '/login':
        return <LoginForm config={config} />;
      case '/reset-password':
        return <ResetPassword />;
      case '/accept-invitation':
        return <AcceptInvitation />;
      case '/auth/callback':
        return <OAuthCallback />;
      default:
        // Check if it starts with any public route
        if (currentPath.startsWith('/login')) {
          return <LoginForm config={config} />;
        }
        if (currentPath.startsWith('/reset-password')) {
          return <ResetPassword />;
        }
        if (currentPath.startsWith('/accept-invitation')) {
          return <AcceptInvitation />;
        }
        if (currentPath.startsWith('/auth/callback')) {
          return <OAuthCallback />;
        }
        return <LoginForm config={config} />;
    }
  }
  
  // If not authenticated (and not on public route - will redirect via useEffect above)
  if (!isAuthenticated) {
    return <LoginForm config={config} />;
  }

  const pluginSidebarItems = pluginRegistry.getAllSidebarItems();

  const renderContent = () => {
    // Core routes
    switch (currentPath) {
      case '/':
        return <HomeDashboard />;
      case '/chat':
        return <ChatInterface />;
      case '/search':
        return <UniversalSearch />;
      case '/data':
        return <DataManagementDashboard />;
      case '/knowledge':
        return <KnowledgeBase />;
      case '/entities':
        return config.features.entities ? <EntityExplorer /> : <div>Entities disabled</div>;
      case '/reports':
        if (!config.features.reports) return <div>Reports disabled</div>;
        // Universal reports page - shows list and redirects to plugin for creation
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Reports</h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">View and manage all reports across projects</p>
              </div>
              <Button onClick={() => {
                // Navigate to plugin-specific reports for creation
                const pluginRoutes = pluginRegistry.getAllRoutes();
                const reportsRoute = pluginRoutes.find(r => r.path.includes('reports'));
                if (reportsRoute) {
                  handleNavigate(reportsRoute.path);
                } else {
                  handleNavigate('/minescope/reports');
                }
              }}>
                Create New Report
              </Button>
            </div>
            <ReportsList 
              onViewReport={(reportId) => {
                // View in plugin context if available
                const pluginRoutes = pluginRegistry.getAllRoutes();
                const reportsRoute = pluginRoutes.find(r => r.path.includes('reports'));
                if (reportsRoute) {
                  handleNavigate(`${reportsRoute.path}?view=${reportId}`);
                }
              }} 
              onCreateNew={() => {
                const pluginRoutes = pluginRegistry.getAllRoutes();
                const reportsRoute = pluginRoutes.find(r => r.path.includes('reports'));
                handleNavigate(reportsRoute?.path || '/minescope/reports');
              }}
            />
          </div>
        );
      case '/sources':
        return config.features.sources ? <SourceList /> : <div>Sources disabled</div>;
      case '/jobs':
        return config.features.jobs ? <JobList /> : <div>Jobs disabled</div>;
      case '/settings':
        return <Settings />;
      case '/profile':
        return <Profile config={config} />;
      case '/teams':
        return <TeamManagement />;
      case '/audit':
        return <AuditLogsViewer />;
      case '/evidence':
        return <EvidenceViewer />;
      case '/policy':
        return <PolicyManager />;
      case '/notifications':
        return <NotificationCenter />;
      case '/notifications/preferences':
        return <NotificationPreferences />;
      default:
        // Check plugin routes (strip query params for matching)
        const pluginRoutes = pluginRegistry.getAllRoutes();
        const pathWithoutQuery = currentPath.split('?')[0];
        const matchedRoute = pluginRoutes.find(r => {
          const matches = pathWithoutQuery.startsWith(r.path);
          if (matches && import.meta.env.DEV) {
            console.log(`[Route Match] Matched ${r.path} for ${pathWithoutQuery}`);
          }
          return matches;
        });
        if (matchedRoute) {
          const Component = matchedRoute.component;
          if (!Component) {
            console.error(`[Route Error] Component is undefined for route ${matchedRoute.path}`);
            return (
              <div className="flex flex-col items-center justify-center h-full py-20">
                <div className="text-6xl mb-4">500</div>
                <div className="text-xl text-gray-600 mb-4">Component not found for route: {matchedRoute.path}</div>
                <Button onClick={() => handleNavigate('/')}>Go Home</Button>
              </div>
            );
          }
          try {
            return <Component />;
          } catch (error: any) {
            console.error(`[Route Error] Error rendering component for ${matchedRoute.path}:`, error);
            return (
              <div className="flex flex-col items-center justify-center h-full py-20">
                <div className="text-6xl mb-4">500</div>
                <div className="text-xl text-gray-600 mb-4">Error rendering component: {error.message}</div>
                <Button onClick={() => handleNavigate('/')}>Go Home</Button>
              </div>
            );
          }
        }
        return (
          <div className="flex flex-col items-center justify-center h-full py-20">
            <div className="text-6xl mb-4">404</div>
            <div className="text-xl text-gray-600 mb-4">Page not found</div>
            <Button onClick={() => handleNavigate('/')}>Go Home</Button>
          </div>
        );
    }
  };

  return (
    <AppShell
      pluginSidebarItems={pluginSidebarItems}
      onNavigate={handleNavigate}
      currentPath={currentPath}
      defaultTenantId={config.customization?.defaultTenantId}
      defaultProjectId={config.customization?.defaultProjectId}
      config={config}
    >
      {renderContent()}
    </AppShell>
  );
}

export interface TraceKitProps {
  config: TraceKitConfig;
}

export function App({ config }: TraceKitProps) {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider config={config}>
          <ThemeProvider config={config}>
            <AuthProvider>
              <SearchConfigProvider>
                <EntityConfigProvider>
                  <PluginContextProvider>
                    <PluginManager plugins={config.plugins} config={config}>
                      <AppContent config={config} />
                    </PluginManager>
                  </PluginContextProvider>
                </EntityConfigProvider>
              </SearchConfigProvider>
            </AuthProvider>
          </ThemeProvider>
        </ConfigProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

